import { useCallback, useEffect, useRef, useState } from 'react'
import { API_ENDPOINTS } from '../../config/api'
import { getActiveOrgId, withOrganizationQuery } from '../../utils/orgUtils'
import type {
  AiCisoConversationMode,
  ConversationMessage,
  ConversationModeRuntime,
  ConversationSession,
  PendingDisclosure,
} from './types'
import { DEFAULT_SCOPE } from './types'
import { isGenerationOutbound, sanitizeMessageForDisplay } from './conversationHelpers'

/** SEC-004.3F — always scope AI conversation APIs to the active organization. */
function aiOrgUrl(url: string, organizationId?: string | null): string {
  return withOrganizationQuery(url, organizationId ?? getActiveOrgId())
}

type TranslateFn = (key: string, fallback?: string) => string

export type DefendSphereAiConversationApi = {
  sessions: ConversationSession[]
  selectedId: string | null
  activeSession: ConversationSession | null
  messages: ConversationMessage[]
  suggestedPrompts: string[]
  loadingSessions: boolean
  loadingDetail: boolean
  sending: boolean
  error: string | null
  draft: string
  optionalFilter: string
  showArchived: boolean
  pendingDisclosure: PendingDisclosure | null
  disclosureBusy: boolean
  editUserPrompt: string
  editContextSummary: string
  editIncludePlatform: boolean
  editIncludeDocs: boolean
  editIncludeContext: boolean
  showTransmitted: boolean
  orgId: string | null
  setDraft: (v: string) => void
  setOptionalFilter: (v: string) => void
  setShowArchived: (v: boolean) => void
  setSelectedId: (id: string | null) => void
  setEditUserPrompt: (v: string) => void
  setEditContextSummary: (v: string) => void
  setEditIncludePlatform: (v: boolean) => void
  setEditIncludeDocs: (v: boolean) => void
  setEditIncludeContext: (v: boolean) => void
  setShowTransmitted: (v: boolean) => void
  setError: (v: string | null) => void
  clearPendingDisclosure: () => void
  loadSessions: () => Promise<void>
  loadSessionDetail: (sessionId: string) => Promise<void>
  createSession: () => Promise<string | null>
  sendMessage: (
    text: string,
    options?: { narrativePreference?: 'deterministic'; dispatchTarget?: AiCisoConversationMode },
  ) => Promise<void>
  handleOutboundSaveEdits: () => Promise<void>
  handleDisclosureApprove: (conversationScope: boolean) => Promise<void>
  handleDisclosureDecline: () => Promise<void>
  archiveSession: () => Promise<void>
  resetForOrganizationChange: () => void
  modeRuntime: ConversationModeRuntime | null
  modeBusy: boolean
  updateConversationMode: (
    mode: AiCisoConversationMode,
    options?: { grantExternalConsent?: boolean; revokeExternalConsent?: boolean },
  ) => Promise<void>
  grantExternalConsent: () => Promise<void>
}

export function useDefendSphereAiConversation(
  token: string | null,
  enabled: boolean,
  t: TranslateFn,
): DefendSphereAiConversationApi {
  const [sessions, setSessions] = useState<ConversationSession[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [activeSession, setActiveSession] = useState<ConversationSession | null>(null)
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([])
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [optionalFilter, setOptionalFilter] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [pendingDisclosure, setPendingDisclosure] = useState<PendingDisclosure | null>(null)
  const [disclosureBusy, setDisclosureBusy] = useState(false)
  const [editUserPrompt, setEditUserPrompt] = useState('')
  const [editContextSummary, setEditContextSummary] = useState('')
  const [editIncludePlatform, setEditIncludePlatform] = useState(true)
  const [editIncludeDocs, setEditIncludeDocs] = useState(true)
  const [editIncludeContext, setEditIncludeContext] = useState(true)
  const [showTransmitted, setShowTransmitted] = useState(false)
  const [orgId, setOrgId] = useState<string | null>(() => getActiveOrgId())
  const [modeRuntime, setModeRuntime] = useState<ConversationModeRuntime | null>(null)
  const [modeBusy, setModeBusy] = useState(false)

  const abortRef = useRef<AbortController | null>(null)
  const dispatchInFlightRef = useRef(false)
  const selectedIdRef = useRef<string | null>(null)
  selectedIdRef.current = selectedId

  const preferenceKey = useCallback((activeOrg: string | null) => {
    return activeOrg ? `ds-ai-selected-session:${activeOrg}` : null
  }, [])

  const rememberSelectedId = useCallback(
    (sessionId: string | null, activeOrg: string | null = orgId) => {
      const key = preferenceKey(activeOrg)
      if (!key || typeof sessionStorage === 'undefined') return
      try {
        if (sessionId) sessionStorage.setItem(key, sessionId)
        else sessionStorage.removeItem(key)
      } catch {
        // ignore quota / private mode
      }
    },
    [orgId, preferenceKey],
  )

  const readRememberedSelectedId = useCallback(
    (activeOrg: string | null) => {
      const key = preferenceKey(activeOrg)
      if (!key || typeof sessionStorage === 'undefined') return null
      try {
        return sessionStorage.getItem(key)
      } catch {
        return null
      }
    },
    [preferenceKey],
  )

  const resetConversationState = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    dispatchInFlightRef.current = false
    setSessions([])
    setSelectedId(null)
    setActiveSession(null)
    setMessages([])
    setSuggestedPrompts([])
    setPendingDisclosure(null)
    setDraft('')
    setError(null)
    setSending(false)
    setDisclosureBusy(false)
    setLoadingSessions(false)
    setLoadingDetail(false)
    setModeRuntime(null)
    setModeBusy(false)
  }, [])

  const resetForOrganizationChange = useCallback(() => {
    resetConversationState()
    setOrgId(getActiveOrgId())
  }, [resetConversationState])

  const applyPendingFromPayload = useCallback(
    (sessionId: string, pending: any, messagesList: ConversationMessage[]) => {
      if (pending?.required && pending?.requestId) {
        const lastAssistant = [...(messagesList || [])]
          .reverse()
          .find((m) => m.role === 'assistant')
        if (lastAssistant?.id) {
          setPendingDisclosure({
            ...pending,
            sessionId,
            messageId: lastAssistant.id,
          })
          setEditUserPrompt(pending.editable?.userPrompt || pending.preview?.question || '')
          setEditContextSummary(pending.editable?.contextSummary || '')
          setEditIncludePlatform(pending.editable?.includePlatformData !== false)
          setEditIncludeDocs(pending.editable?.includeDocumentRag !== false)
          setEditIncludeContext(pending.editable?.includeContext !== false)
          return
        }
      }
    },
    [],
  )

  const loadSessions = useCallback(async () => {
    if (!token || !enabled) return
    setLoadingSessions(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (showArchived) qs.set('includeArchived', 'true')
      const response = await fetch(
        aiOrgUrl(`${API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_SESSIONS}?${qs}`, getActiveOrgId()),
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload?.message || `Failed to load sessions (${response.status})`)
      const rows = Array.isArray(payload?.data) ? payload.data : []
      setSessions(rows)
      if (!selectedIdRef.current && rows.length) {
        const remembered = readRememberedSelectedId(getActiveOrgId())
        const match = remembered && rows.some((r: ConversationSession) => r.id === remembered)
        setSelectedId(match ? remembered : rows[0].id)
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') return
      setError(e?.message || 'Failed to load conversations.')
      setSessions([])
    } finally {
      setLoadingSessions(false)
    }
  }, [token, enabled, showArchived, readRememberedSelectedId])

  useEffect(() => {
    rememberSelectedId(selectedId, orgId)
  }, [selectedId, orgId, rememberSelectedId])

  const loadSessionDetail = useCallback(
    async (sessionId: string) => {
      if (!token || !enabled) return
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setLoadingDetail(true)
      setError(null)
      try {
        const response = await fetch(
          aiOrgUrl(API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_SESSION(sessionId), getActiveOrgId()),
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        )
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(payload?.message || `Failed to load session (${response.status})`)
        if (controller.signal.aborted) return
        const session = payload?.data?.session
        const msgs: ConversationMessage[] = Array.isArray(session?.messages) ? session.messages : []
        const runtime = (payload?.data?.modeRuntime || session?.modeRuntime || null) as ConversationModeRuntime | null
        setActiveSession(session ? { ...session, modeRuntime: runtime } : null)
        setModeRuntime(runtime)
        setMessages(msgs)
        setSuggestedPrompts(Array.isArray(payload?.data?.suggestedPrompts) ? payload.data.suggestedPrompts : [])
        applyPendingFromPayload(sessionId, payload?.data?.pendingOutbound, msgs)
      } catch (e: any) {
        if (e?.name === 'AbortError') return
        setError(e?.message || 'Failed to load conversation.')
        setMessages([])
        setActiveSession(null)
      } finally {
        if (!controller.signal.aborted) setLoadingDetail(false)
      }
    },
    [token, enabled, applyPendingFromPayload],
  )

  useEffect(() => {
    if (!enabled || !token) return
    const current = getActiveOrgId()
    if (current !== orgId) {
      resetForOrganizationChange()
    }
  }, [enabled, token, orgId, resetForOrganizationChange])

  useEffect(() => {
    if (!enabled || !token) return
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === 'user') {
        const next = getActiveOrgId()
        if (next !== orgId) resetForOrganizationChange()
      }
    }
    window.addEventListener('storage', onStorage)
    const interval = window.setInterval(() => {
      const next = getActiveOrgId()
      if (next !== orgId) resetForOrganizationChange()
    }, 2000)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.clearInterval(interval)
    }
  }, [enabled, token, orgId, resetForOrganizationChange])

  useEffect(() => {
    if (!enabled || !token) return
    void loadSessions()
  }, [enabled, token, loadSessions, orgId])

  useEffect(() => {
    if (!enabled || !token || !selectedId) return
    void loadSessionDetail(selectedId)
  }, [enabled, token, selectedId, loadSessionDetail, orgId])

  const createSession = useCallback(async () => {
    if (!token) return null
    setError(null)
    try {
      const body: Record<string, string> = { contextScope: optionalFilter || DEFAULT_SCOPE }
      const response = await fetch(aiOrgUrl(API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_SESSIONS, getActiveOrgId()), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload?.message || 'Failed to create session')
      const session = payload?.data?.session
      const runtime = (payload?.data?.modeRuntime || session?.modeRuntime || null) as ConversationModeRuntime | null
      if (session?.id) {
        setSelectedId(session.id)
        setActiveSession(session ? { ...session, modeRuntime: runtime } : null)
        setModeRuntime(runtime)
        setSuggestedPrompts(payload?.data?.suggestedPrompts || [])
        setMessages([])
        setPendingDisclosure(null)
      }
      await loadSessions()
      return session?.id || null
    } catch (e: any) {
      setError(e?.message || 'Failed to create conversation.')
      return null
    }
  }, [token, optionalFilter, loadSessions])

  const updateConversationMode = useCallback(
    async (
      mode: AiCisoConversationMode,
      options?: { grantExternalConsent?: boolean; revokeExternalConsent?: boolean },
    ) => {
      if (!token || !selectedId || modeBusy) return
      setModeBusy(true)
      setError(null)
      try {
        const body: Record<string, unknown> = { conversationMode: mode }
        if (options?.grantExternalConsent === true) body.grantExternalConsent = true
        if (options?.revokeExternalConsent === true) body.revokeExternalConsent = true
        const response = await fetch(aiOrgUrl(API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_SESSION_MODE(selectedId), getActiveOrgId()), {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(payload?.message || payload?.code || 'Failed to update conversation mode')
        const session = payload?.data?.session
        const runtime = (payload?.data?.modeRuntime || session?.modeRuntime || null) as ConversationModeRuntime | null
        if (session) {
          setActiveSession({ ...session, modeRuntime: runtime })
        }
        setModeRuntime(runtime)
      } catch (e: any) {
        setError(e?.message || 'Failed to update conversation mode.')
      } finally {
        setModeBusy(false)
      }
    },
    [token, selectedId, modeBusy],
  )

  const grantExternalConsent = useCallback(async () => {
    const mode = (activeSession?.conversationMode || modeRuntime?.conversationMode || 'EXTERNAL_AI_RAG') as AiCisoConversationMode
    await updateConversationMode(
      mode === 'EXTERNAL_AI_RAG' ? mode : 'EXTERNAL_AI_RAG',
      { grantExternalConsent: true },
    )
  }, [activeSession?.conversationMode, modeRuntime?.conversationMode, updateConversationMode])

  const sendMessage = useCallback(
    async (
      text: string,
      options?: { narrativePreference?: 'deterministic'; dispatchTarget?: AiCisoConversationMode },
    ) => {
      if (!selectedId || !text.trim() || !token || sending) return
      setSending(true)
      setError(null)
      try {
        const response = await fetch(
          aiOrgUrl(API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_MESSAGES(selectedId), getActiveOrgId()),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              content: text.trim(),
              narrativePreference: options?.narrativePreference || undefined,
              // Per-turn route only — does not PATCH session conversationMode / consent
              dispatchTarget: options?.dispatchTarget || undefined,
            }),
          },
        )
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
          const code = payload?.code ? ` (${payload.code})` : ''
          throw new Error((payload?.message || 'Failed to send message') + code)
        }
        const disclosure = payload?.data?.outboundPreview || payload?.data?.disclosureRequired
        const assistantId = payload?.data?.assistantMessage?.id
        if (disclosure?.required && disclosure.requestId && assistantId) {
          setPendingDisclosure({
            ...disclosure,
            sessionId: selectedId,
            messageId: assistantId,
          })
          setEditUserPrompt(disclosure.editable?.userPrompt || disclosure.preview?.question || '')
          setEditContextSummary(disclosure.editable?.contextSummary || '')
          setEditIncludePlatform(disclosure.editable?.includePlatformData !== false)
          setEditIncludeDocs(disclosure.editable?.includeDocumentRag !== false)
          setEditIncludeContext(disclosure.editable?.includeContext !== false)
        } else {
          setPendingDisclosure(null)
        }
        await loadSessionDetail(selectedId)
        await loadSessions()
        setDraft('')
      } catch (e: any) {
        setError(e?.message || 'Failed to send message.')
      } finally {
        setSending(false)
      }
    },
    [selectedId, token, sending, loadSessionDetail, loadSessions],
  )

  const handleOutboundSaveEdits = useCallback(async () => {
    if (!pendingDisclosure?.requestId || !token || !isGenerationOutbound(pendingDisclosure)) return
    setDisclosureBusy(true)
    setError(null)
    try {
      const res = await fetch(aiOrgUrl(API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_DISCLOSURE(pendingDisclosure.requestId), getActiveOrgId()), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userPrompt: editUserPrompt,
          contextSummary: editContextSummary,
          includePlatformData: editIncludePlatform,
          includeDocumentRag: editIncludeDocs,
          includeContext: editIncludeContext,
        }),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload?.message || payload?.code || 'Failed to save outbound edits')
      const data = payload?.data || {}
      setPendingDisclosure((prev) =>
        prev
          ? {
              ...prev,
              ...data,
              sessionId: prev.sessionId,
              messageId: prev.messageId,
            }
          : prev,
      )
      setEditUserPrompt(data.editable?.userPrompt || editUserPrompt)
      setEditContextSummary(data.editable?.contextSummary || editContextSummary)
    } catch (e: any) {
      setError(e?.message || 'Failed to revalidate edited outbound payload.')
    } finally {
      setDisclosureBusy(false)
    }
  }, [
    pendingDisclosure,
    token,
    editUserPrompt,
    editContextSummary,
    editIncludePlatform,
    editIncludeDocs,
    editIncludeContext,
  ])

  const mapOutboundError = useCallback(
    (raw: string) => {
      if (/DISPATCH_AMBIGUOUS|DISPATCH_FAILED|PROVIDER_TIMEOUT|CONNECTION_RESET/i.test(raw)) {
        return t('complianceCenterUi.aiCiso.errorDispatchFailed')
      }
      if (/EXPIRED|APPROVAL_EXPIRED/i.test(raw)) return t('complianceCenterUi.aiCiso.errorApprovalExpired')
      if (/PAYLOAD_CHANGED|PAYLOAD_MISMATCH/i.test(raw)) return t('complianceCenterUi.aiCiso.errorPayloadChanged')
      if (/ALREADY_CONSUMED|already used/i.test(raw)) return t('complianceCenterUi.aiCiso.errorAlreadyConsumed')
      if (/POLICY_BLOCKED/i.test(raw)) return t('complianceCenterUi.aiCiso.errorPolicyBlocked')
      return raw || t('complianceCenterUi.aiCiso.errorGeneric')
    },
    [t],
  )

  const handleDisclosureApprove = useCallback(
    async (conversationScope: boolean) => {
      if (!pendingDisclosure?.requestId || !pendingDisclosure.sessionId || !pendingDisclosure.messageId || !token) {
        return
      }
      if (dispatchInFlightRef.current || disclosureBusy) return
      dispatchInFlightRef.current = true
      setDisclosureBusy(true)
      setError(null)
      const generation = isGenerationOutbound(pendingDisclosure)
      try {
        if (generation) {
          const editRes = await fetch(
            aiOrgUrl(
              API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_DISCLOSURE(pendingDisclosure.requestId),
              getActiveOrgId(),
            ),
            {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userPrompt: editUserPrompt,
                contextSummary: editContextSummary,
                includePlatformData: editIncludePlatform,
                includeDocumentRag: editIncludeDocs,
                includeContext: editIncludeContext,
              }),
            },
          )
          const editPayload = await editRes.json().catch(() => ({}))
          if (!editRes.ok) {
            throw new Error(editPayload?.message || editPayload?.code || 'Failed to revalidate outbound edits')
          }
          if (editPayload?.data) {
            setPendingDisclosure((prev) =>
              prev
                ? { ...prev, ...editPayload.data, sessionId: prev.sessionId, messageId: prev.messageId }
                : prev,
            )
          }
        }

        const approveRes = await fetch(
          aiOrgUrl(
            API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_DISCLOSURE_APPROVE(pendingDisclosure.requestId),
            getActiveOrgId(),
          ),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ conversationScope }),
          },
        )
        const approvePayload = await approveRes.json().catch(() => ({}))
        if (!approveRes.ok) throw new Error(approvePayload?.message || 'Failed to approve disclosure')

        if (generation) {
          const dispatchRes = await fetch(
            aiOrgUrl(
              API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_OUTBOUND_DISPATCH(
                pendingDisclosure.sessionId,
                pendingDisclosure.messageId,
              ),
              getActiveOrgId(),
            ),
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                disclosureRequestId: pendingDisclosure.requestId,
                conversationScope,
              }),
            },
          )
          const dispatchPayload = await dispatchRes.json().catch(() => ({}))
          if (!dispatchRes.ok) {
            throw new Error(dispatchPayload?.message || dispatchPayload?.code || 'Failed to dispatch outbound request')
          }
          // Replace interim APPROVAL_REQUIRED / INSUFFICIENT message with finalized outbound answer
          const finalized = dispatchPayload?.data?.assistantMessage
          if (finalized?.id) {
            const sanitized = sanitizeMessageForDisplay(finalized as ConversationMessage)
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === sanitized.id)
              if (idx < 0) return [...prev, sanitized]
              const next = [...prev]
              next[idx] = sanitized
              return next
            })
          }
          setPendingDisclosure(null)
          await loadSessionDetail(pendingDisclosure.sessionId)
        } else {
          const enhanceRes = await fetch(
            aiOrgUrl(
              API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_NARRATIVE_ENHANCE(
                pendingDisclosure.sessionId,
                pendingDisclosure.messageId,
              ),
              getActiveOrgId(),
            ),
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                disclosureRequestId: pendingDisclosure.requestId,
                conversationScope,
              }),
            },
          )
          const enhancePayload = await enhanceRes.json().catch(() => ({}))
          if (!enhanceRes.ok) throw new Error(enhancePayload?.message || 'Failed to apply narrative enhancement')
          const enhanced = enhancePayload?.data?.assistantMessage
          if (enhanced?.id) {
            const sanitized = sanitizeMessageForDisplay(enhanced as ConversationMessage)
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === sanitized.id)
              if (idx < 0) return [...prev, sanitized]
              const next = [...prev]
              next[idx] = sanitized
              return next
            })
          }
          setPendingDisclosure(null)
          await loadSessionDetail(pendingDisclosure.sessionId)
        }
      } catch (e: any) {
        setError(mapOutboundError(String(e?.message || '')))
      } finally {
        dispatchInFlightRef.current = false
        setDisclosureBusy(false)
      }
    },
    [
      pendingDisclosure,
      token,
      disclosureBusy,
      editUserPrompt,
      editContextSummary,
      editIncludePlatform,
      editIncludeDocs,
      editIncludeContext,
      loadSessionDetail,
      mapOutboundError,
    ],
  )

  const handleDisclosureDecline = useCallback(async () => {
    if (!pendingDisclosure?.requestId) {
      setPendingDisclosure(null)
      return
    }
    if (!token) return
    setDisclosureBusy(true)
    try {
      const endpoint = aiOrgUrl(
        isGenerationOutbound(pendingDisclosure)
          ? API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_DISCLOSURE_CANCEL(pendingDisclosure.requestId)
          : API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_DISCLOSURE_DECLINE(pendingDisclosure.requestId),
        getActiveOrgId(),
      )
      await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
    } catch {
      // deterministic answer already shown
    } finally {
      setPendingDisclosure(null)
      setDisclosureBusy(false)
    }
  }, [pendingDisclosure, token])

  const archiveSession = useCallback(async () => {
    if (!selectedId || !token) return
    try {
      const response = await fetch(aiOrgUrl(API_ENDPOINTS.AI_GOVERNANCE_CONVERSATION_ARCHIVE(selectedId), getActiveOrgId()), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload?.message || 'Failed to archive')
      setSelectedId(null)
      setActiveSession(null)
      setMessages([])
      setPendingDisclosure(null)
      await loadSessions()
    } catch (e: any) {
      setError(e?.message || 'Failed to archive conversation.')
    }
  }, [selectedId, token, loadSessions])

  const clearPendingDisclosure = useCallback(() => setPendingDisclosure(null), [])

  const selectSession = useCallback(
    (id: string | null) => {
      setSelectedId(id)
      rememberSelectedId(id, orgId)
    },
    [orgId, rememberSelectedId],
  )

  return {
    sessions,
    selectedId,
    activeSession,
    messages,
    suggestedPrompts,
    loadingSessions,
    loadingDetail,
    sending,
    error,
    draft,
    optionalFilter,
    showArchived,
    pendingDisclosure,
    disclosureBusy,
    editUserPrompt,
    editContextSummary,
    editIncludePlatform,
    editIncludeDocs,
    editIncludeContext,
    showTransmitted,
    orgId,
    setDraft,
    setOptionalFilter,
    setShowArchived,
    setSelectedId: selectSession,
    setEditUserPrompt,
    setEditContextSummary,
    setEditIncludePlatform,
    setEditIncludeDocs,
    setEditIncludeContext,
    setShowTransmitted,
    setError,
    clearPendingDisclosure,
    loadSessions,
    loadSessionDetail,
    createSession,
    sendMessage,
    handleOutboundSaveEdits,
    handleDisclosureApprove,
    handleDisclosureDecline,
    archiveSession,
    resetForOrganizationChange,
    modeRuntime,
    modeBusy,
    updateConversationMode,
    grantExternalConsent,
  }
}
